'use client'

import { Suspense, useRef, useState } from 'react'
import Pagination from '../Pagination'

import Manual from '../Manual'
import type { ManualsSectionFragment } from '@/lib/types/datocms/graphql/generated'
import { TextWithHighlight } from '../TextWithHighlight'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function ManualsSection({ manuals, title }: Readonly<ManualsSectionFragment>) {
	const manualsRef = useRef<HTMLDivElement>(null)
	const [currentPage, setCurrentPage] = useState(1)
	const itemsPerPage = 6

	// Calculate pagination values
	const totalPages = Math.ceil(manuals.length / itemsPerPage)
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = Math.min(startIndex + itemsPerPage, manuals.length) // Ensure we don't exceed total length
	const currentManuals = manuals.slice(startIndex, endIndex)

	const handlePageChange = (page: number) => {
		setCurrentPage(page)

		setTimeout(() => {
			if (manualsRef.current) {
				manualsRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}
		}, 0)
	}

	return (
		<section className="manuals-section @container">
			<div className="common-x-padding max-w-screen-2xl mx-auto py-16 @5xl:pt-24 @5xl:pb-28">
				{title && !isStructuredTextEmpty(title.text) && (
					<div className="heading-size-3 mt-6 xl:mt-10 mb-6">
						<TextWithHighlight text={title} />
					</div>
				)}
				<h3 className="heading-size-5 mb-10" ref={manualsRef}>
					Showing{' '}
					<span className="text-blue">
						{startIndex + 1}-{endIndex}
					</span>{' '}
					of <span className="text-blue">{manuals.length}</span> results
				</h3>
				<div className="divide-y">
					{currentManuals.map((manual) => (
						<Manual key={manual.id as string} manual={manual} />
					))}
				</div>
				{totalPages > 1 && (
					<Suspense fallback={null}>
						<Pagination
							className="mt-8"
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={handlePageChange}
						/>
					</Suspense>
				)}
			</div>
		</section>
	)
}
